/**
 * A single task card in the feed. Renders one of four states from the design
 * review: suggested action, requires attention, Storyboard resolved (purple),
 * and auto-resolved (green). Once a dispatcher acts, the card moves to "done".
 */

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BoltIcon from "@mui/icons-material/Bolt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoneIcon from "@mui/icons-material/Done";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SendIcon from "@mui/icons-material/Send";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { accent, text, zinc } from "../colors";
import type { DispatchTask, Driver } from "../types";
import { ActionButton, Chip, UrgencyBadge } from "./primitives";

interface TaskCardProps {
  task: DispatchTask;
  driver: Driver;
  selected: boolean;
  onSelect: () => void;
  onSend: () => void;
  onMarkResolved: () => void;
}

function accentForState(state: DispatchTask["state"]): string {
  switch (state) {
    case "requires_attention":
      return accent.amber;
    case "storyboard_resolved":
      return accent.purple;
    case "awaiting_response":
      return accent.blue;
    case "auto_resolved":
    case "done":
      return accent.green;
    default:
      return accent.blue;
  }
}

export function TaskCard({ task, driver, selected, onSelect, onSend, onMarkResolved }: TaskCardProps) {
  const dimmed = task.state === "auto_resolved" || task.state === "storyboard_resolved" || task.state === "done";
  const leftAccent = accentForState(task.state);

  return (
    <Box
      onClick={onSelect}
      sx={{
        cursor: "pointer",
        position: "relative",
        bgcolor: selected ? zinc[850] : zinc[900],
        border: `1px solid ${selected ? leftAccent : zinc[800]}`,
        borderLeft: `3px solid ${leftAccent}`,
        borderRadius: "10px",
        p: 1.75,
        opacity: dimmed ? 0.82 : 1,
        transition: "background 120ms ease, border-color 120ms ease",
        "&:hover": { bgcolor: zinc[850], borderColor: selected ? leftAccent : zinc[700], borderLeftColor: leftAccent },
      }}
    >
      {/* header: driver + status badge */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.75 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: text.primary }}>
          {driver.name}{" "}
          <Box component="span" sx={{ color: text.muted, fontWeight: 600 }}>
            · Truck {driver.truck}
          </Box>
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {task.state === "suggested" && task.urgency && <UrgencyBadge urgency={task.urgency} />}
          {task.state === "requires_attention" && (
            <>
              <Chip label={task.type === "empty_call_failed" ? "PREPLAN FAILED" : "REQUIRES ATTENTION"} tone="amber" />
              <WarningAmberIcon sx={{ fontSize: 18, color: accent.amber }} />
            </>
          )}
          {task.state === "awaiting_response" && (
            <>
              <Chip label="AWAITING DRIVER RESPONSE" tone="blue" />
              <HourglassEmptyIcon sx={{ fontSize: 17, color: accent.blue }} />
            </>
          )}
          {task.state === "storyboard_resolved" && (
            <>
              <Chip label="STORYBOARD RESOLVED" tone="purple" />
              <CheckCircleIcon sx={{ fontSize: 18, color: accent.purple }} />
            </>
          )}
          {(task.state === "auto_resolved" || task.state === "done") && (
            <>
              <Chip label={task.state === "done" ? "Resolved" : "Auto-resolved"} tone="green" />
              <CheckCircleIcon sx={{ fontSize: 18, color: accent.green }} />
            </>
          )}
        </Box>
      </Box>

      {/* assigned-to-me badge */}
      {task.assignedToMe && task.assignedBy && (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
          <PersonOutlineIcon sx={{ fontSize: 14, color: accent.purple }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: accent.purple }}>
            Assigned to you by {task.assignedBy}
          </Typography>
        </Box>
      )}

      {/* situation summary */}
      <Typography
        sx={{
          fontSize: 13.5,
          color: text.secondary,
          lineHeight: 1.45,
          mb: task.recommendation || task.sentMessage || task.resolvedNote ? 0.75 : 1.25,
        }}
      >
        {task.summary}
      </Typography>

      {/* recommendation row (suggested) */}
      {task.recommendation && (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mb: 0.5 }}>
          <BoltIcon sx={{ fontSize: 17, color: accent.amber, mt: "1px" }} />
          <Typography sx={{ fontSize: 13.5, color: text.primary }}>
            <Box component="span" sx={{ color: text.muted }}>
              Storyboard suggests:{" "}
            </Box>
            {task.recommendation}
          </Typography>
        </Box>
      )}

      {/* canned message preview (suggested) */}
      {task.cannedMessage && (
        <Typography sx={{ fontSize: 13, fontStyle: "italic", color: text.muted, mb: 1.25 }}>
          “{task.cannedMessage}”
        </Typography>
      )}

      {/* requires attention note — only when there's no specific recommendation */}
      {task.state === "requires_attention" && !task.recommendation && (
        <Typography sx={{ fontSize: 13, fontStyle: "italic", color: accent.amber, mb: 1.25 }}>
          No suggested action — needs your review.
        </Typography>
      )}

      {/* awaiting driver response note */}
      {task.state === "awaiting_response" && (
        <Typography sx={{ fontSize: 13, fontStyle: "italic", color: accent.blue, mb: 1.25 }}>
          Message sent — awaiting driver response.
        </Typography>
      )}

      {/* storyboard sent message */}
      {task.sentMessage && (
        <Typography sx={{ fontSize: 13, fontStyle: "italic", color: text.muted, mb: 1 }}>
          Storyboard sent: “{task.sentMessage}”
        </Typography>
      )}

      {/* auto-resolved note */}
      {task.resolvedNote && (
        <Typography sx={{ fontSize: 13, fontStyle: "italic", color: accent.green }}>{task.resolvedNote}</Typography>
      )}

      {/* actions */}
      {task.state === "suggested" && (
        <Box sx={{ display: "flex", gap: 1 }} onClick={(e) => e.stopPropagation()}>
          <ActionButton label="Send message" icon={<SendIcon sx={{ fontSize: 15 }} />} onClick={onSend} />
          <ActionButton
            label="Mark resolved"
            icon={<DoneIcon sx={{ fontSize: 16 }} />}
            variant="ghost"
            onClick={onMarkResolved}
          />
        </Box>
      )}
      {(task.state === "requires_attention" || task.state === "awaiting_response") && (
        <Box sx={{ display: "flex", gap: 1 }} onClick={(e) => e.stopPropagation()}>
          <ActionButton label="Mark resolved" icon={<DoneIcon sx={{ fontSize: 16 }} />} onClick={onMarkResolved} />
        </Box>
      )}
      {task.state === "storyboard_resolved" && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <AutoAwesomeIcon sx={{ fontSize: 15, color: accent.purple }} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: accent.purple }}>Storyboard</Typography>
        </Box>
      )}
    </Box>
  );
}
